import { useState, useEffect } from 'react'
import Button from '@/components/Button'
import { PageMetadata } from '@/types/page'

export const metadata: PageMetadata = {
  id: 'conversation-metadata',
  category: 'messaging',
  categoryName: 'Messaging Widget',
  name: 'Conversation Metadata',
  icon: '🏷️',
  path: '/messaging/conversation-metadata',
  title: 'Zendesk Conversation Metadata',
  description: 'Pass Free-Form Key-Value Context Data'
}

interface MetadataEntry {
  key: string
  value: string
}

export default function ConversationMetadata() {
  const [metadataEntries, setMetadataEntries] = useState<MetadataEntry[]>([
    { key: '', value: '' }
  ])
  const [applied, setApplied] = useState(false)

  useEffect(() => {
    document.title = `Internal Note - ${metadata.title}`

    // Add Zendesk widget script
    const script = document.createElement('script')
    script.id = 'ze-snippet'
    script.src = 'https://static.zdassets.com/ekr/snippet.js?key=e125418a-9466-44d8-9b3f-2ac10e911ea4'
    script.async = true
    document.body.appendChild(script)

    return () => {
      const existing = document.getElementById('ze-snippet')
      if (existing) existing.remove()
    }
  }, [])

  const addMetadataEntry = () => {
    setMetadataEntries([...metadataEntries, { key: '', value: '' }])
  }

  const removeMetadataEntry = (index: number) => {
    const newEntries = metadataEntries.filter((_, i) => i !== index)
    setMetadataEntries(newEntries.length > 0 ? newEntries : [{ key: '', value: '' }])
  }

  const updateMetadataEntry = (index: number, field: 'key' | 'value', newValue: string) => {
    const newEntries = [...metadataEntries]
    newEntries[index][field] = newValue
    setMetadataEntries(newEntries)
  }

  const handleApplyMetadata = () => {
    // Filter out empty entries
    const validEntries = metadataEntries.filter(entry => entry.key.trim() !== '' && entry.value.trim() !== '')

    if (validEntries.length === 0) {
      alert('Please add at least one key-value pair')
      return
    }

    // Convert to object
    const metadataObject: Record<string, string> = {}
    validEntries.forEach(entry => {
      metadataObject[entry.key.trim()] = entry.value.trim()
    })

    if (window.zE) {
      window.zE('messenger:set', 'conversationMetadata', metadataObject)
      setApplied(true)
      setTimeout(() => setApplied(false), 3000)

      // Open the widget to show the metadata is set
      window.zE('messenger', 'open')
    }
  }

  const handleClear = () => {
    setMetadataEntries([{ key: '', value: '' }])
    setApplied(false)
  }

  const loadExample = (example: 'ecommerce' | 'support' | 'marketing') => {
    const examples = {
      ecommerce: [
        { key: 'cart_value', value: '149.99' },
        { key: 'items_in_cart', value: '3' },
        { key: 'last_product_viewed', value: 'Wireless Headphones' },
        { key: 'customer_segment', value: 'premium' }
      ],
      support: [
        { key: 'account_type', value: 'enterprise' },
        { key: 'subscription_status', value: 'active' },
        { key: 'last_login', value: '2024-01-15' },
        { key: 'support_tier', value: 'priority' }
      ],
      marketing: [
        { key: 'campaign_source', value: 'google_ads' },
        { key: 'utm_medium', value: 'cpc' },
        { key: 'landing_page', value: '/pricing' },
        { key: 'referral_code', value: 'SAVE20' }
      ]
    }
    setMetadataEntries(examples[example])
  }

  return (
    <>
      <header>
        <div className="mx-auto max-w-7xl py-6 px-4 sm:px-6 md:px-8">
          <div className="md:flex md:items-center md:justify-between md:space-x-5">
            <div>
              <h1 className="text-3xl text-licorice dark:text-white">{metadata.title}</h1>
              <h2 className="mt-2 text-xl text-licorice dark:text-white">{metadata.description}</h2>
            </div>
            <div className="justify-stretch mt-6 flex flex-col-reverse space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-y-0 sm:space-x-3 sm:space-x-reverse md:mt-0 md:flex-row md:space-x-3">
              <Button
                as="a"
                href="https://developer.zendesk.com/documentation/zendesk-web-widget-sdks/sdks/web/sdk_api_reference/#set-conversation-metadata"
                target="_blank"
                rel="noopener noreferrer"
              >
                API Reference
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="mt-8 mb-32">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          {/* Introduction */}
          <div className="mb-8">
            <p className="text-base leading-7 text-gray-600 dark:text-white mb-4">
              Use <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-sm">conversationMetadata</code> to pass free-form key-value pairs to agents.
              Unlike structured ticket fields, metadata allows flexible custom context without pre-configuration.
            </p>
          </div>

          {/* Comparison Table */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-licorice dark:text-white mb-4">When to Use What?</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-900/50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">API</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Use Case</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Demo Page</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-licorice dark:text-white">conversationFields</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">Structured</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">Pre-defined ticket fields</td>
                      <td className="px-4 py-3 text-sm"><a href="/messaging/metadata" className="text-blue-600 dark:text-blue-400 underline">Metadata</a></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-licorice dark:text-white">conversationTags</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">Array</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">Categorization & routing</td>
                      <td className="px-4 py-3 text-sm"><a href="/proactive/contact" className="text-blue-600 dark:text-blue-400 underline">Contact-based</a></td>
                    </tr>
                    <tr className="bg-blue-50 dark:bg-blue-900/20">
                      <td className="px-4 py-3 text-sm font-mono text-licorice dark:text-white font-bold">conversationMetadata</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">Key-Value</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">Free-form custom context</td>
                      <td className="px-4 py-3 text-sm text-blue-600 dark:text-blue-400 font-semibold">This Page</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Load Example Buttons */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-licorice dark:text-white mb-3">Load Example Scenario:</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => loadExample('ecommerce')}
                className="px-4 py-2 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50 text-sm"
              >
                🛒 E-commerce
              </button>
              <button
                onClick={() => loadExample('support')}
                className="px-4 py-2 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 text-sm"
              >
                🎫 Support Context
              </button>
              <button
                onClick={() => loadExample('marketing')}
                className="px-4 py-2 bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 rounded-lg hover:bg-orange-200 dark:hover:bg-orange-900/50 text-sm"
              >
                📊 Marketing Attribution
              </button>
            </div>
          </div>

          {/* Metadata Builder */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <h3 className="text-lg font-semibold text-licorice dark:text-white mb-4">Metadata Builder</h3>

            <div className="space-y-3">
              {metadataEntries.map((entry, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <input
                    type="text"
                    placeholder="Key (e.g., user_tier)"
                    value={entry.key}
                    onChange={(e) => updateMetadataEntry(index, 'key', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-licorice dark:text-white text-sm"
                  />
                  <span className="text-gray-400">:</span>
                  <input
                    type="text"
                    placeholder="Value (e.g., premium)"
                    value={entry.value}
                    onChange={(e) => updateMetadataEntry(index, 'value', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-licorice dark:text-white text-sm"
                  />
                  <button
                    onClick={() => removeMetadataEntry(index)}
                    className="px-3 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md"
                    disabled={metadataEntries.length === 1}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            <div className="flex gap-2 mt-4">
              <button
                onClick={addMetadataEntry}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-licorice dark:text-white rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 text-sm"
              >
                + Add Entry
              </button>
              <button
                onClick={handleApplyMetadata}
                className="px-6 py-2 bg-matcha text-licorice dark:text-white rounded-md hover:bg-lime font-semibold text-sm"
              >
                Apply & Open Widget
              </button>
              <button
                onClick={handleClear}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-licorice dark:text-white rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 text-sm"
              >
                Clear All
              </button>
            </div>
          </div>

          {/* Success Message */}
          {applied && (
            <div className="bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6">
              <p className="text-sm text-green-800 dark:text-green-200">
                ✅ <strong>Metadata Applied!</strong> The key-value pairs have been set on the conversation. Agents will see this context when they view the ticket.
              </p>
            </div>
          )}

          {/* Preview */}
          <div className="bg-gray-900 rounded-lg p-6 mb-6">
            <h4 className="text-sm font-semibold text-gray-100 mb-3">API Call Preview:</h4>
            <pre className="text-sm text-gray-100 overflow-x-auto">
              <code>
                {`zE('messenger:set', 'conversationMetadata', ${JSON.stringify(
                  metadataEntries
                    .filter(e => e.key.trim() && e.value.trim())
                    .reduce((acc, e) => ({ ...acc, [e.key.trim()]: e.value.trim() }), {}),
                  null,
                  2
                )})`}
              </code>
            </pre>
          </div>

          {/* Use Cases */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
            <h4 className="font-semibold text-licorice dark:text-white mb-3">Common Use Cases</h4>
            <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2 list-disc list-inside">
              <li><strong>E-commerce:</strong> Cart value, items in cart, last product viewed, customer segment</li>
              <li><strong>SaaS:</strong> Account type, subscription status, feature usage, API calls remaining</li>
              <li><strong>Marketing:</strong> Campaign source, UTM parameters, referral code, landing page</li>
              <li><strong>Support:</strong> Previous ticket count, last contact date, account age, priority level</li>
              <li><strong>User Journey:</strong> Pages visited, time on site, actions taken, funnel stage</li>
            </ul>
          </div>
        </div>
      </div>
    </>
  )
}

// Extend window type for Zendesk
declare global {
  interface Window {
    zE?: any
  }
}
